// Settings → Organizations panel. Lists every org the current user is
// a member of with lifecycle controls: hide, archive, delete. Embedded
// inside the Settings page as a tab; bypasses the SettingsEnvelope
// pattern since orgs aren't a "setting" — they're first-class
// resources at /api/v1/orgs.
//
// Lifecycle ladder (orthogonal states):
//   normal   → hidden (UI preference; reversible via PATCH visibility)
//            → archived (system lock; mutations 423 LOCKED; reversible
//                        via POST /archive {archive:false})
//            → deactivated (terminal soft-delete via DELETE /orgs/:id)
//
// Cap-gated server-side: orgs:hide / orgs:archive / orgs:manage_members,
// OR per-org owner. UI shows the buttons to everyone; server returns
// 403 if the caller can't act. (Hiding buttons advisorily would require
// querying caps + per-org owner flags; one round-trip per action is the
// simpler shape today.)

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { apiFetch, apiList, apiSingle } from '../lib/api.js';
import { copyToClipboard } from '../lib/clipboard.js';
import { toast } from './Toast.js';
import { EyeOff, Eye, Archive, ArchiveRestore, Trash2, UserPlus, Plus, Copy, ArrowRightLeft, Pencil, UserMinus } from 'lucide-react';
import { UserTypeahead, type UserLite } from './UserTypeahead.js';

// Live slug derived from a name. Lowercase, hyphens for non-alphanumeric,
// strip leading/trailing hyphens, cap at 64. Mirrors the server-side
// slugify() in apps/api/src/routes/organizations.ts; doing it client-side
// gives the operator a live preview as they type the name.
function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}

interface RoleOption {
  id: string;
  name: string;
  description: string | null;
  scope: 'org' | 'global';
  isBuiltIn: boolean;
}

interface OrgMembership {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  isActive: boolean;        // active-org-of-session flag (overloaded; see /orgs route)
  isHidden: boolean;
  archivedAt: string | null;
  myMembership: {
    isOwner: boolean;
    joinedAt: string;
  };
}

interface OrgMember {
  orgId: string;
  userId: string;
  roleName: string;
  isOwner: boolean;
  joinedAt: string;
  user: { id: string; displayName: string; email: string; isActive: boolean };
}

export function OrganizationsPanel() {
  const qc = useQueryClient();
  const [showHidden, setShowHidden] = useState(false);

  // ── Create user form state ─────────────────────────────────────
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserDisplayName, setNewUserDisplayName] = useState('');
  // The temp password from the server, surfaced in a one-time
  // banner. Cleared when the operator dismisses it OR opens a
  // fresh Create-user form.
  const [revealedPassword, setRevealedPassword] = useState<{
    email: string;
    password: string;
  } | null>(null);

  // ── Create org form state ──────────────────────────────────────
  const [createOrgOpen, setCreateOrgOpen] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [newOrgSlug, setNewOrgSlug] = useState('');
  const [newOrgDescription, setNewOrgDescription] = useState('');
  // True when the operator has hand-edited the slug, so we stop
  // auto-syncing it to the name. Without this, typing in name
  // would clobber a manual slug they liked.
  const [slugDirty, setSlugDirty] = useState(false);

  const createUserMut = useMutation({
    mutationFn: () =>
      apiSingle<{
        id: string;
        email: string;
        displayName: string;
        tempPassword?: string;
      }>('/users', {
        method: 'POST',
        body: {
          email: newUserEmail.trim().toLowerCase(),
          displayName: newUserDisplayName.trim() || undefined,
        },
      }),
    onSuccess: (data) => {
      // If the API generated the password, reveal it once. Otherwise
      // (operator supplied their own — not currently exposed in the
      // form), no banner is needed.
      if (data.tempPassword) {
        setRevealedPassword({ email: data.email, password: data.tempPassword });
      }
      toast.success(`User created: ${data.email}`);
      setCreateUserOpen(false);
      setNewUserEmail('');
      setNewUserDisplayName('');
      qc.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err: unknown) => toast.fromError(err, 'Failed to create user'),
  });

  const createOrgMut = useMutation({
    mutationFn: () =>
      apiSingle<OrgMembership>('/orgs', {
        method: 'POST',
        body: {
          name: newOrgName.trim(),
          slug: newOrgSlug.trim() || undefined,
          description: newOrgDescription.trim() || undefined,
        },
      }),
    onSuccess: (data) => {
      toast.success(`Org created: ${data.name}`);
      setCreateOrgOpen(false);
      setNewOrgName('');
      setNewOrgSlug('');
      setNewOrgDescription('');
      setSlugDirty(false);
      qc.invalidateQueries({ queryKey: ['my-orgs'] });
    },
    onError: (err: unknown) => toast.fromError(err, 'Failed to create org'),
  });

  const orgsQ = useQuery({
    queryKey: ['my-orgs', { includeHidden: showHidden }],
    queryFn: () => apiList<OrgMembership>('/orgs', {
      query: { includeHidden: showHidden ? 'true' : undefined },
    }),
  });

  const invalidateOrgs = () => {
    qc.invalidateQueries({ queryKey: ['my-orgs'] });
  };

  const hideMut = useMutation({
    mutationFn: ({ orgId, isHidden }: { orgId: string; isHidden: boolean }) =>
      apiSingle<OrgMembership>(`/orgs/${orgId}/visibility`, {
        method: 'PATCH',
        body: { isHidden },
      }),
    onSuccess: (_data, { isHidden }) => {
      toast.success(isHidden ? 'Org hidden from default listing' : 'Org unhidden');
      // Keep the just-hidden row visible so the operator can undo
      // without hunting for it. Without this, hide → row disappears
      // (because the default ?includeHidden filter excludes it) and
      // the only way back is to manually toggle Show hidden. Flipping
      // it on automatically when hide happens turns the action into
      // a clear toggle with a one-click undo via the Eye icon on
      // the now-visible row.
      if (isHidden && !showHidden) setShowHidden(true);
      invalidateOrgs();
    },
    onError: (err: unknown) => toast.fromError(err, 'Failed to update visibility'),
  });

  const archiveMut = useMutation({
    mutationFn: ({ orgId, archive }: { orgId: string; archive: boolean }) =>
      apiSingle<OrgMembership>(`/orgs/${orgId}/archive`, {
        method: 'POST',
        body: { archive },
      }),
    onSuccess: (_data, { archive }) => {
      toast.success(archive ? 'Org archived (read-only)' : 'Org unarchived');
      invalidateOrgs();
    },
    onError: (err: unknown) => toast.error(err instanceof Error ? err.message : 'Failed to update archive state'),
  });

  const deleteMut = useMutation({
    mutationFn: (orgId: string) =>
      apiFetch<void>(`/orgs/${orgId}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('Org deactivated');
      invalidateOrgs();
    },
    onError: (err: unknown) => toast.error(err instanceof Error ? err.message : 'Failed to deactivate org'),
  });

  if (orgsQ.isLoading) {
    return <div style={{ color: 'var(--text-muted)' }}>Loading organizations…</div>;
  }
  if (orgsQ.isError) {
    return <div style={{ color: 'var(--danger-primary, #B3261E)' }}>Failed to load organizations.</div>;
  }

  const orgs = orgsQ.data?.data ?? [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* One-time password reveal. Stays mounted until the operator
          dismisses it — the API never returns this password again, so
          the operator has to capture it now (clipboard / password
          manager / hand-off). Renders above everything else so it's
          impossible to miss. */}
      {revealedPassword && (
        <section
          role="alert"
          style={{
            background: 'color-mix(in srgb, var(--warning-primary) 12%, var(--surface-raised))',
            border: '1px solid var(--warning-primary)',
            borderRadius: 'var(--radius-md)',
            padding: 16,
            color: 'var(--text-primary)',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
            <strong style={{ fontSize: 14, color: 'var(--warning-primary)' }}>
              One-time password for {revealedPassword.email}
            </strong>
            <button
              type="button"
              onClick={() => setRevealedPassword(null)}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--text-muted)',
                cursor: 'pointer',
                fontSize: 12,
              }}
            >
              Dismiss
            </button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <code
              style={{
                flex: 1,
                fontFamily: 'var(--font-mono)',
                fontSize: 14,
                padding: '8px 12px',
                background: 'var(--surface-raised)',
                border: '1px solid var(--border-strong)',
                borderRadius: 4,
                userSelect: 'all',
              }}
            >
              {revealedPassword.password}
            </code>
            <button
              type="button"
              onClick={() => {
                // CTSK-282: copyToClipboard falls back to execCommand on
                // insecure origins (raw navigator.clipboard fails on http://).
                void copyToClipboard(revealedPassword.password).then((ok) => {
                  if (ok) toast.success('Password copied');
                  else toast.error('Clipboard write failed — select the text manually');
                });
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                padding: '8px 12px',
                fontSize: 12,
                border: '1px solid var(--border-strong)',
                borderRadius: 4,
                background: 'var(--surface-raised)',
                cursor: 'pointer',
              }}
            >
              <Copy size={12} /> Copy
            </button>
          </div>
          <p style={{ margin: 0, fontSize: 11, color: 'var(--text-muted)' }}>
            This is the only time the password will be shown. Hand it off to {revealedPassword.email} —
            they should change it on first login.
          </p>
        </section>
      )}

      <section style={{ background: 'var(--surface-raised)', padding: 20, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <h2 style={{ margin: 0, fontSize: 16 }}>Your organizations</h2>
          {/* Show-hidden toggle. Drives the ?includeHidden=true param
              on /orgs so hidden orgs appear in the list when the
              operator wants to manage them. */}
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-secondary)', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={showHidden}
              onChange={(e) => setShowHidden(e.target.checked)}
            />
            Show hidden
          </label>
        </div>
        <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 0 }}>
          You're a member of {orgs.length} organization{orgs.length === 1 ? '' : 's'}.
          Use the org switcher in the topbar to change which one's active.
        </p>

        <table className="grid">
          <thead>
            <tr>
              <th style={{ width: 28 }}></th>
              <th>Name</th>
              <th style={{ width: 140 }}>Slug</th>
              <th style={{ width: 100 }}>Role</th>
              <th style={{ width: 110 }}>State</th>
              <th style={{ width: 220, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {orgs.map((o) => {
              const isArchived = !!o.archivedAt;
              const busy = hideMut.isPending || archiveMut.isPending || deleteMut.isPending;
              return (
                <tr key={o.id}>
                  <td style={{ textAlign: 'center', color: 'var(--accent-primary)' }}>
                    {o.isActive ? '●' : ''}
                  </td>
                  <td>
                    <div style={{ fontWeight: 500 }}>{o.name}</div>
                    {o.description && (
                      <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>{o.description}</div>
                    )}
                  </td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{o.slug}</td>
                  <td style={{ fontSize: 12 }}>
                    {o.myMembership.isOwner ? (
                      <span style={{ color: 'var(--accent-primary)', fontWeight: 600 }}>owner</span>
                    ) : (
                      <span style={{ color: 'var(--text-muted)' }}>member</span>
                    )}
                  </td>
                  <td style={{ fontSize: 11 }}>
                    {isArchived && (
                      <span title={`Archived ${new Date(o.archivedAt!).toLocaleString()}`} style={{ display: 'inline-block', padding: '2px 6px', borderRadius: 4, background: 'color-mix(in srgb, var(--warning-primary) 14%, var(--surface-raised))', color: 'var(--warning-primary)', fontWeight: 600, marginRight: 4 }}>
                        Archived
                      </span>
                    )}
                    {o.isHidden && (
                      <span title="Hidden from default listing" style={{ display: 'inline-block', padding: '2px 6px', borderRadius: 4, background: 'var(--surface-sunken)', color: 'var(--text-muted)' }}>
                        Hidden
                      </span>
                    )}
                    {!isArchived && !o.isHidden && (
                      <span style={{ color: 'var(--text-muted)' }}>—</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <div style={{ display: 'inline-flex', gap: 4 }}>
                      <IconBtn
                        // Icon mirrors the CURRENT state, not the
                        // action: open eye when the org is visible,
                        // crossed-out eye when it's hidden. Click
                        // toggles the state. Matches the password-
                        // field convention every user already knows.
                        title={o.isHidden ? 'Click to unhide (currently hidden)' : 'Click to hide from default listing'}
                        onClick={() => hideMut.mutate({ orgId: o.id, isHidden: !o.isHidden })}
                        disabled={busy}
                      >
                        {o.isHidden ? <EyeOff size={14} /> : <Eye size={14} />}
                      </IconBtn>
                      <IconBtn
                        // Action-based icons (the pair depicts the verb,
                        // not a state — ArchiveRestore literally shows
                        // an arrow pulling something OUT of a box).
                        //   not archived → Archive icon: "click to archive"
                        //   archived → ArchiveRestore icon: "click to restore"
                        // Hide icons above use state-based convention
                        // because Eye / EyeOff are about visibility, not
                        // about an action verb. Tooltip carries the
                        // action verbally either way.
                        title={isArchived ? 'Click to unarchive (currently archived, read-only)' : 'Click to archive (will lock writes)'}
                        onClick={() => archiveMut.mutate({ orgId: o.id, archive: !isArchived })}
                        disabled={busy}
                      >
                        {isArchived ? <ArchiveRestore size={14} /> : <Archive size={14} />}
                      </IconBtn>
                      <IconBtn
                        title="Deactivate (soft-delete)"
                        danger
                        onClick={() => {
                          if (confirm(`Deactivate '${o.name}'? Data is preserved; org disappears from listings. Default org cannot be deactivated.`)) {
                            deleteMut.mutate(o.id);
                          }
                        }}
                        disabled={busy || o.slug === 'default'}
                      >
                        <Trash2 size={14} />
                      </IconBtn>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      {/* Show membership rosters for orgs where the user is an owner. */}
      {orgs.filter((o) => o.myMembership.isOwner && !o.archivedAt).map((o) => (
        <MembersPanel
          key={o.id}
          orgId={o.id}
          orgName={o.name}
          // CTSK-242 — candidate target orgs for the transfer action:
          // every non-archived org the operator can see, minus the
          // source itself. Server enforces both-side authority; this
          // list is a UI convenience, not an authorization surface.
          otherOrgs={orgs
            .filter((x) => x.id !== o.id && !x.archivedAt)
            .map((x) => ({ id: x.id, name: x.name }))}
        />
      ))}

      {/* Create user + Create org — two parallel admin affordances.
          Both gated server-side: POST /users on users:create (held by
          emissary / steward / envoy / owner); POST /orgs on
          orgs:create (held by emissary / envoy / owner). The buttons
          render unconditionally — the server returns 403 if the caller
          lacks the cap, surfaced via toast.fromError. */}
      <section style={{ background: 'var(--surface-raised)', padding: 20, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
          <h2 style={{ margin: 0, fontSize: 16 }}>Create new</h2>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            User + org provisioning
          </span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {/* ─── Create user ─── */}
          <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: 16, background: 'var(--surface-sunken)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <h3 style={{ margin: 0, fontSize: 14 }}>Create user</h3>
              {!createUserOpen && (
                <button
                  type="button"
                  onClick={() => setCreateUserOpen(true)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '4px 10px',
                    fontSize: 12,
                    border: '1px solid var(--border-strong)',
                    borderRadius: 6,
                    background: 'var(--surface-raised)',
                    color: 'var(--text-primary)',
                    cursor: 'pointer',
                  }}
                >
                  <UserPlus size={12} /> New user
                </button>
              )}
            </div>
            {createUserOpen ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!newUserEmail.trim()) return;
                  createUserMut.mutate();
                }}
                style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
              >
                <input
                  type="email"
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                  placeholder="user@example.com"
                  required
                  autoFocus
                  style={{
                    padding: '6px 10px',
                    fontSize: 13,
                    border: '1px solid var(--border-strong)',
                    borderRadius: 4,
                    fontFamily: 'var(--font-mono)',
                  }}
                />
                <input
                  type="text"
                  value={newUserDisplayName}
                  onChange={(e) => setNewUserDisplayName(e.target.value)}
                  placeholder="Display name (optional — derives from email)"
                  style={{
                    padding: '6px 10px',
                    fontSize: 13,
                    border: '1px solid var(--border-strong)',
                    borderRadius: 4,
                  }}
                />
                <p style={{ margin: 0, fontSize: 11, color: 'var(--text-muted)' }}>
                  A one-time password is generated; you'll see it once.
                </p>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    type="submit"
                    disabled={createUserMut.isPending || !newUserEmail.trim()}
                    style={{
                      flex: 1,
                      padding: '6px 14px',
                      fontSize: 13,
                      background: 'var(--accent-primary)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 4,
                      cursor: createUserMut.isPending ? 'wait' : 'pointer',
                      fontWeight: 600,
                    }}
                  >
                    Create
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setCreateUserOpen(false);
                      setNewUserEmail('');
                      setNewUserDisplayName('');
                    }}
                    style={{
                      padding: '6px 14px',
                      fontSize: 13,
                      background: 'transparent',
                      color: 'var(--text-muted)',
                      border: '1px solid var(--border-subtle)',
                      borderRadius: 4,
                      cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <p style={{ margin: 0, fontSize: 12, color: 'var(--text-muted)' }}>
                Mint a new user account with an auto-generated temp password.
                Use the "Add member" form on any org's panel to attach them.
              </p>
            )}
          </div>

          {/* ─── Create org ─── */}
          <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: 16, background: 'var(--surface-sunken)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <h3 style={{ margin: 0, fontSize: 14 }}>Create organization</h3>
              {!createOrgOpen && (
                <button
                  type="button"
                  onClick={() => setCreateOrgOpen(true)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '4px 10px',
                    fontSize: 12,
                    border: '1px solid var(--border-strong)',
                    borderRadius: 6,
                    background: 'var(--surface-raised)',
                    color: 'var(--text-primary)',
                    cursor: 'pointer',
                  }}
                >
                  <Plus size={12} /> New org
                </button>
              )}
            </div>
            {createOrgOpen ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!newOrgName.trim()) return;
                  createOrgMut.mutate();
                }}
                style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
              >
                <input
                  type="text"
                  value={newOrgName}
                  onChange={(e) => {
                    setNewOrgName(e.target.value);
                    // Auto-sync slug from name UNLESS operator has
                    // already hand-edited the slug (slugDirty flag).
                    if (!slugDirty) setNewOrgSlug(slugify(e.target.value));
                  }}
                  placeholder="Mission Control"
                  required
                  autoFocus
                  style={{
                    padding: '6px 10px',
                    fontSize: 13,
                    border: '1px solid var(--border-strong)',
                    borderRadius: 4,
                  }}
                />
                <input
                  type="text"
                  value={newOrgSlug}
                  onChange={(e) => {
                    setNewOrgSlug(e.target.value);
                    setSlugDirty(true);
                  }}
                  placeholder="mission-control"
                  pattern="^[a-z0-9][a-z0-9-]*$"
                  title="Lowercase letters, digits, and hyphens; start with a letter or digit"
                  style={{
                    padding: '6px 10px',
                    fontSize: 13,
                    border: '1px solid var(--border-strong)',
                    borderRadius: 4,
                    fontFamily: 'var(--font-mono)',
                  }}
                />
                <textarea
                  value={newOrgDescription}
                  onChange={(e) => setNewOrgDescription(e.target.value)}
                  placeholder="Description (optional)"
                  rows={2}
                  style={{
                    padding: '6px 10px',
                    fontSize: 12,
                    border: '1px solid var(--border-strong)',
                    borderRadius: 4,
                    resize: 'vertical',
                    fontFamily: 'inherit',
                  }}
                />
                <p style={{ margin: 0, fontSize: 11, color: 'var(--text-muted)' }}>
                  You become the owner automatically. Add members via the org's
                  panel after creation. For starter containers/tags/rules, use
                  the bootstrap CLI.
                </p>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    type="submit"
                    disabled={createOrgMut.isPending || !newOrgName.trim()}
                    style={{
                      flex: 1,
                      padding: '6px 14px',
                      fontSize: 13,
                      background: 'var(--accent-primary)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 4,
                      cursor: createOrgMut.isPending ? 'wait' : 'pointer',
                      fontWeight: 600,
                    }}
                  >
                    Create
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setCreateOrgOpen(false);
                      setNewOrgName('');
                      setNewOrgSlug('');
                      setNewOrgDescription('');
                      setSlugDirty(false);
                    }}
                    style={{
                      padding: '6px 14px',
                      fontSize: 13,
                      background: 'transparent',
                      color: 'var(--text-muted)',
                      border: '1px solid var(--border-subtle)',
                      borderRadius: 4,
                      cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <p style={{ margin: 0, fontSize: 12, color: 'var(--text-muted)' }}>
                Mint a new tenant. For richer starter payloads
                (containers + tags + rules), use{' '}
                <code style={{ fontSize: 11 }}>scripts/bootstrap-org.ts</code>{' '}
                instead — see <code>docs/vault-setup.md</code>.
              </p>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

// Small icon button styled to match the rest of the admin pages.
// Server-side cap checks are authoritative; this button always
// renders. If the caller lacks the cap, the server returns 403
// (or 423 LOCKED if the org is archived) and the toast surfaces it.
function IconBtn({
  children,
  onClick,
  title,
  disabled,
  danger,
}: {
  children: React.ReactNode;
  onClick: () => void;
  title: string;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      disabled={disabled}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 28,
        height: 28,
        padding: 0,
        background: 'transparent',
        border: '1px solid var(--border-subtle)',
        borderRadius: 4,
        color: danger ? 'var(--danger-primary, #B3261E)' : 'var(--text-secondary)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
      }}
    >
      {children}
    </button>
  );
}

function MembersPanel({
  orgId,
  orgName,
  otherOrgs,
}: {
  orgId: string;
  orgName: string;
  otherOrgs: { id: string; name: string }[];
}) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [roleInput, setRoleInput] = useState<string>('contributor');
  const [isOwnerInput, setIsOwnerInput] = useState(false);

  // ── CTSK-242: transfer-to-another-org flow ─────────────────────
  // transferFor holds the member being transferred; null = closed.
  const [transferFor, setTransferFor] = useState<OrgMember | null>(null);
  const [transferOrgId, setTransferOrgId] = useState('');
  const [transferRole, setTransferRole] = useState<string>('contributor');
  const [keepOldMembership, setKeepOldMembership] = useState(false);

  // Impact preview (AC5) — informational only. If this query errors
  // (or is slow), the confirmation still renders with an em-dash;
  // a preview failure must never block the transfer itself.
  const impactQ = useQuery({
    queryKey: ['transfer-impact', orgId, transferFor?.userId],
    queryFn: () =>
      apiSingle<{ workItemCount: number; commentCount: number }>(
        `/orgs/${orgId}/memberships/${transferFor!.userId}/transfer-impact`,
      ),
    enabled: !!transferFor,
    retry: false,
  });

  const closeTransfer = () => {
    setTransferFor(null);
    setTransferOrgId('');
    setKeepOldMembership(false);
  };

  // ── CTSK-242 smoke follow-up (2026-07-30): change-role + remove ─
  // The server has always exposed PATCH /orgs/:id/memberships/:userId
  // and DELETE /orgs/:id/memberships/:userId (see routes/organizations.ts
  // §PATCH+DELETE). Operator smoke on 0.1.87 confirmed the UI only
  // exposed Transfer, which forces a shell-game to change a role and
  // has no path at all to just detach a member. Adding both actions as
  // sibling buttons in the same row (not a kebab) matches the rest of
  // the app's row-actions pattern.
  const [roleEditFor, setRoleEditFor] = useState<OrgMember | null>(null);
  const [roleEditValue, setRoleEditValue] = useState<string>('contributor');
  const [roleEditIsOwner, setRoleEditIsOwner] = useState<boolean>(false);

  const closeRoleEdit = () => {
    setRoleEditFor(null);
  };

  const roleEditMut = useMutation({
    mutationFn: () => {
      const roleId = orgRoles.find((r) => r.name === roleEditValue)?.id;
      if (!roleId) throw new Error(`Unknown role: ${roleEditValue}`);
      // PATCH accepts { roleId?, isOwner? }. We send both so a single
      // form can demote-owner-and-change-role in one round trip. Server
      // last-owner guard (422) protects the unsafe demotion case.
      return apiSingle<unknown>(`/orgs/${orgId}/memberships/${roleEditFor!.userId}`, {
        method: 'PATCH',
        body: { roleId, isOwner: roleEditIsOwner },
      });
    },
    onSuccess: () => {
      toast.success(`Updated role for ${roleEditFor?.user.email}`);
      qc.invalidateQueries({ queryKey: ['org-members', orgId] });
      closeRoleEdit();
    },
    onError: (err: unknown) => toast.fromError(err, 'Failed to update role'),
  });

  const removeMut = useMutation({
    mutationFn: (userId: string) =>
      apiFetch<void>(`/orgs/${orgId}/memberships/${userId}`, {
        method: 'DELETE',
      }),
    onSuccess: (_d, userId) => {
      toast.success(`Removed member from ${orgName}`);
      qc.invalidateQueries({ queryKey: ['org-members', orgId] });
      void userId;
    },
    onError: (err: unknown) => toast.fromError(err, 'Failed to remove member'),
  });

  const transferMut = useMutation({
    mutationFn: () =>
      apiSingle<unknown>(`/orgs/${orgId}/memberships/${transferFor!.userId}/transfer`, {
        method: 'POST',
        body: {
          targetOrgId: transferOrgId,
          targetRoleName: transferRole,
          keepOldMembership,
        },
      }),
    onSuccess: () => {
      const targetName = otherOrgs.find((o) => o.id === transferOrgId)?.name ?? 'target org';
      toast.success(`Transferred ${transferFor?.user.email} to ${targetName}`);
      qc.invalidateQueries({ queryKey: ['org-members', orgId] });
      qc.invalidateQueries({ queryKey: ['org-members', transferOrgId] });
      closeTransfer();
    },
    onError: (err: unknown) => toast.fromError(err, 'Transfer failed'),
  });

  const membersQ = useQuery({
    queryKey: ['org-members', orgId],
    queryFn: () => apiList<OrgMember>(`/orgs/${orgId}/memberships`),
  });

  // Role list — only org-scoped roles are valid in the org-memberships
  // table. Global roles (emissary / steward / envoy) get managed
  // separately in Settings → Global Roles.
  const rolesQ = useQuery({
    queryKey: ['roles'],
    queryFn: () => apiList<RoleOption>('/roles'),
  });
  const orgRoles = (rolesQ.data?.data ?? []).filter((r) => r.scope === 'org');

  const addMut = useMutation({
    mutationFn: () =>
      apiSingle<unknown>(`/orgs/${orgId}/memberships`, {
        method: 'POST',
        body: {
          email: emailInput.trim(),
          roleName: roleInput,
          isOwner: isOwnerInput,
        },
      }),
    onSuccess: () => {
      toast.success(`Added ${emailInput.trim()} to ${orgName}`);
      setAdding(false);
      setEmailInput('');
      setIsOwnerInput(false);
      qc.invalidateQueries({ queryKey: ['org-members', orgId] });
    },
    onError: (err: unknown) => toast.fromError(err, 'Failed to add member'),
  });

  const members = membersQ.data?.data ?? [];
  const owners = members.filter((m) => m.isOwner);
  const nonOwners = members.filter((m) => !m.isOwner);

  return (
    <section style={{ background: 'var(--surface-raised)', padding: 20, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 4 }}>
        <h2 style={{ margin: 0, fontSize: 16 }}>{orgName} — members</h2>
        <button
          type="button"
          onClick={() => setAdding((v) => !v)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '4px 10px',
            fontSize: 12,
            border: '1px solid var(--border-strong)',
            borderRadius: 6,
            background: 'var(--surface-raised)',
            color: 'var(--text-primary)',
            cursor: 'pointer',
          }}
        >
          <UserPlus size={12} /> {adding ? 'Cancel' : 'Add member'}
        </button>
      </div>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 0 }}>
        {members.length} member{members.length === 1 ? '' : 's'} ·
        {' '}{owners.length} owner{owners.length === 1 ? '' : 's'}
      </p>

      {/* Inline add form. Email + role picker + isOwner. Submit fires
          POST /orgs/:id/memberships with { email, roleName, isOwner }
          — the API resolves email → userId and roleName → roleId.
          CTSK-245 (0.1.83): raw email <input> replaced with UserTypeahead.
          Picker populates emailInput (backward-compat with the API's
          email → userId resolution) so no server-side change needed on
          the /orgs/:id/memberships path. Operators who know the email
          can still type it directly — the picker's dropdown is
          empty-state-friendly and Enter still submits the form. */}
      {adding && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!emailInput.trim()) return;
            addMut.mutate();
          }}
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            alignItems: 'center',
            padding: 12,
            background: 'var(--surface-sunken)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-md)',
            marginBottom: 12,
          }}
        >
          <UserTypeahead
            value={emailInput}
            onChange={setEmailInput}
            onSelect={(u: UserLite) => setEmailInput(u.email)}
            placeholder="Search by name or email…"
            ariaLabel="Search users to add to this organization"
            autoFocus
            disabled={addMut.isPending}
            inputStyle={{ fontFamily: 'var(--font-mono)' }}
          />
          <select
            value={roleInput}
            onChange={(e) => setRoleInput(e.target.value)}
            style={{
              padding: '6px 10px',
              fontSize: 13,
              border: '1px solid var(--border-strong)',
              borderRadius: 4,
              background: 'var(--surface-raised)',
            }}
          >
            {orgRoles.length === 0 ? (
              <option value="contributor">contributor</option>
            ) : (
              orgRoles.map((r) => (
                <option key={r.id} value={r.name}>{r.name}</option>
              ))
            )}
          </select>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--text-secondary)' }}>
            <input
              type="checkbox"
              checked={isOwnerInput}
              onChange={(e) => setIsOwnerInput(e.target.checked)}
            />
            owner
          </label>
          <button
            type="submit"
            disabled={addMut.isPending || !emailInput.trim()}
            style={{
              padding: '6px 14px',
              fontSize: 13,
              background: 'var(--accent-primary)',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              cursor: addMut.isPending ? 'wait' : 'pointer',
              fontWeight: 600,
            }}
          >
            Add
          </button>
        </form>
      )}

      {membersQ.isLoading && <div style={{ color: 'var(--text-muted)' }}>Loading…</div>}

      {members.length > 0 && (
        // table-layout: fixed pins the column widths to the values in
        // the <colgroup>. Without this, auto-layout was giving the
        // User column more width than its short display names needed
        // and pinching Email so the address wrapped. Fixed layout +
        // overflow:hidden + ellipsis + nowrap = clean truncation.
        <table className="grid" style={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: 160 }} />
            <col style={{ width: 280 }} />
            <col style={{ width: 100 }} />
            <col />
            {/* CTSK-242 smoke follow-up: was 100 (Transfer-only).
                Now fits Role + Remove + Transfer inline. */}
            <col style={{ width: 280 }} />
          </colgroup>
          <thead>
            <tr>
              <th>User</th>
              <th>Email</th>
              <th>Role</th>
              <th>Joined</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {[...owners, ...nonOwners].map((m) => (
              <tr key={m.userId}>
                <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={m.user.displayName}>
                  {m.user.displayName}
                </td>
                <td
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 12,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={m.user.email}
                >
                  {m.user.email}
                </td>
                <td style={{ fontSize: 12 }}>
                  {m.isOwner ? (
                    <span style={{ color: 'var(--accent-primary)', fontWeight: 600 }}>owner</span>
                  ) : (
                    <span style={{ color: 'var(--text-muted)' }}>{m.roleName}</span>
                  )}
                </td>
                <td style={{ color: 'var(--text-muted)', fontSize: 12, whiteSpace: 'nowrap' }}>
                  {new Date(m.joinedAt).toLocaleString()}
                </td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {/* CTSK-242 smoke follow-up (2026-07-30): row actions
                      wrapped in inline-flex, three buttons visible:
                      Role, Remove, Transfer. Last-owner guard is
                      enforced server-side (422); the UI disables Role +
                      Remove for the sole owner so operators don't hit a
                      confusing toast. Transfer stays enabled because
                      transferring away the last owner is legal (server
                      enforces both-side orgs:manage_members). */}
                  <div style={{ display: 'inline-flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}>
                    <button
                      type="button"
                      title={
                        m.isOwner && owners.length === 1
                          ? 'Cannot change the sole owner’s role — promote another owner first'
                          : `Change ${m.user.email}'s role in ${orgName}`
                      }
                      disabled={m.isOwner && owners.length === 1}
                      onClick={() => {
                        setRoleEditFor(m);
                        setRoleEditValue(m.roleName);
                        setRoleEditIsOwner(m.isOwner);
                      }}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        padding: '4px 10px',
                        fontSize: 12,
                        border: '1px solid var(--border-subtle)',
                        borderRadius: 4,
                        background: 'transparent',
                        color: m.isOwner && owners.length === 1 ? 'var(--text-muted)' : 'var(--text-primary)',
                        cursor: m.isOwner && owners.length === 1 ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <Pencil size={12} /> Role
                    </button>
                    <button
                      type="button"
                      title={
                        m.isOwner && owners.length === 1
                          ? 'Cannot remove the sole owner — promote another owner first'
                          : `Remove ${m.user.email} from ${orgName}`
                      }
                      disabled={(m.isOwner && owners.length === 1) || removeMut.isPending}
                      onClick={() => {
                        if (
                          confirm(
                            `Remove ${m.user.email} from ${orgName}?\n\nTheir workitems, comments, and audit trail stay in place. This is recorded in the audit log.`,
                          )
                        ) {
                          removeMut.mutate(m.userId);
                        }
                      }}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        padding: '4px 10px',
                        fontSize: 12,
                        border: '1px solid var(--border-subtle)',
                        borderRadius: 4,
                        background: 'transparent',
                        color: m.isOwner && owners.length === 1 ? 'var(--text-muted)' : 'var(--danger-primary, #B3261E)',
                        cursor:
                          (m.isOwner && owners.length === 1) || removeMut.isPending
                            ? 'not-allowed'
                            : 'pointer',
                      }}
                    >
                      <UserMinus size={12} /> Remove
                    </button>
                    <button
                      type="button"
                      title={
                        otherOrgs.length === 0
                          ? 'No other organization to transfer to'
                          : `Transfer ${m.user.email} to another org`
                      }
                      disabled={otherOrgs.length === 0}
                      onClick={() => {
                        setTransferFor(m);
                        setTransferOrgId(otherOrgs[0]?.id ?? '');
                        setTransferRole('contributor');
                        setKeepOldMembership(false);
                      }}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        padding: '4px 10px',
                        fontSize: 12,
                        border: '1px solid var(--border-subtle)',
                        borderRadius: 4,
                        background: 'transparent',
                        color: otherOrgs.length === 0 ? 'var(--text-muted)' : 'var(--text-primary)',
                        cursor: otherOrgs.length === 0 ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <ArrowRightLeft size={12} /> Transfer
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* CTSK-242 — transfer confirmation. Org picker + role picker +
          impact preview (informational; renders "—" if the preview
          query fails rather than blocking the flow). */}
      {transferFor && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!transferOrgId) return;
            transferMut.mutate();
          }}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            padding: 14,
            marginTop: 12,
            background: 'var(--surface-sunken)',
            border: '1px solid var(--warning-primary)',
            borderRadius: 'var(--radius-md)',
          }}
        >
          <strong style={{ fontSize: 13 }}>
            Transfer {transferFor.user.displayName} ({transferFor.user.email}) out of {orgName}
          </strong>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
            <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              To org{' '}
              <select
                value={transferOrgId}
                onChange={(e) => setTransferOrgId(e.target.value)}
                style={{ padding: '6px 10px', fontSize: 13, border: '1px solid var(--border-strong)', borderRadius: 4, background: 'var(--surface-raised)' }}
              >
                {otherOrgs.map((o) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
            </label>
            <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              As role{' '}
              <select
                value={transferRole}
                onChange={(e) => setTransferRole(e.target.value)}
                style={{ padding: '6px 10px', fontSize: 13, border: '1px solid var(--border-strong)', borderRadius: 4, background: 'var(--surface-raised)' }}
              >
                {orgRoles.length === 0 ? (
                  <option value="contributor">contributor</option>
                ) : (
                  orgRoles.map((r) => (
                    <option key={r.id} value={r.name}>{r.name}</option>
                  ))
                )}
              </select>
            </label>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--text-secondary)' }}>
              <input
                type="checkbox"
                checked={keepOldMembership}
                onChange={(e) => setKeepOldMembership(e.target.checked)}
              />
              keep membership in {orgName}
            </label>
          </div>
          <p style={{ margin: 0, fontSize: 12, color: 'var(--text-muted)' }}>
            Impact in {orgName}:{' '}
            {impactQ.isLoading
              ? 'counting…'
              : impactQ.isError
                ? '— (preview unavailable)'
                : `${impactQ.data?.workItemCount ?? 0} workitem${(impactQ.data?.workItemCount ?? 0) === 1 ? '' : 's'} assigned · ${impactQ.data?.commentCount ?? 0} comment${(impactQ.data?.commentCount ?? 0) === 1 ? '' : 's'} authored`}
            {' '}— history stays in place; the transfer is recorded in the audit log.
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="submit"
              disabled={transferMut.isPending || !transferOrgId}
              style={{
                padding: '6px 14px',
                fontSize: 13,
                background: 'var(--accent-primary)',
                color: '#fff',
                border: 'none',
                borderRadius: 4,
                cursor: transferMut.isPending ? 'wait' : 'pointer',
                fontWeight: 600,
              }}
            >
              {transferMut.isPending ? 'Transferring…' : 'Confirm transfer'}
            </button>
            <button
              type="button"
              onClick={closeTransfer}
              style={{
                padding: '6px 14px',
                fontSize: 13,
                background: 'transparent',
                color: 'var(--text-muted)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 4,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* CTSK-242 smoke follow-up (2026-07-30): change-role confirmation.
          Reuses the transferFor modal's visual shape so operators
          learn one pattern for row-scoped actions. Role picker +
          isOwner toggle; server enforces the "can't demote the last
          owner" guard so we don't try to replicate it here. */}
      {roleEditFor && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!roleEditValue) return;
            roleEditMut.mutate();
          }}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            padding: 14,
            marginTop: 12,
            background: 'var(--surface-sunken)',
            border: '1px solid var(--accent-primary)',
            borderRadius: 'var(--radius-md)',
          }}
        >
          <strong style={{ fontSize: 13 }}>
            Change role for {roleEditFor.user.displayName} ({roleEditFor.user.email}) in {orgName}
          </strong>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
            <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              New role{' '}
              <select
                value={roleEditValue}
                onChange={(e) => setRoleEditValue(e.target.value)}
                style={{ padding: '6px 10px', fontSize: 13, border: '1px solid var(--border-strong)', borderRadius: 4, background: 'var(--surface-raised)' }}
              >
                {orgRoles.length === 0 ? (
                  <option value={roleEditValue}>{roleEditValue}</option>
                ) : (
                  orgRoles.map((r) => (
                    <option key={r.id} value={r.name}>{r.name}</option>
                  ))
                )}
              </select>
            </label>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--text-secondary)' }}>
              <input
                type="checkbox"
                checked={roleEditIsOwner}
                onChange={(e) => setRoleEditIsOwner(e.target.checked)}
              />
              owner (per-org lifecycle + members)
            </label>
          </div>
          <p style={{ margin: 0, fontSize: 12, color: 'var(--text-muted)' }}>
            Effective caps are the union of the org role + any global roles the user holds.
            {' '}Demoting the last owner is blocked server-side; promote another owner first if needed.
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="submit"
              disabled={roleEditMut.isPending}
              style={{
                padding: '6px 14px',
                fontSize: 13,
                background: 'var(--accent-primary)',
                color: '#fff',
                border: 'none',
                borderRadius: 4,
                cursor: roleEditMut.isPending ? 'wait' : 'pointer',
                fontWeight: 600,
              }}
            >
              {roleEditMut.isPending ? 'Saving…' : 'Confirm change'}
            </button>
            <button
              type="button"
              onClick={closeRoleEdit}
              style={{
                padding: '6px 14px',
                fontSize: 13,
                background: 'transparent',
                color: 'var(--text-muted)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 4,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
